import React, { FC } from 'react';

import { Box, Button, Chip, Stack, Typography } from 'components';

import { dayjs, useTranslation } from 'third-party';

import { DISPLAY_DATE_FORMAT } from 'constants/common';

import {
  BillingPlan,
  OrganizationSubscription,
  SubscriptionStatus,
} from 'modules/billing';

type Props = {
  plan: BillingPlan | null;
  subscription: OrganizationSubscription | null;
  isPortalPending: boolean;
  isCancelPending: boolean;
  onOpenPortal: () => void;
  onCancel: () => void;
};

export const SubscriptionPanel: FC<Props> = ({
  plan,
  subscription,
  isPortalPending,
  isCancelPending,
  onOpenPortal,
  onCancel,
}) => {
  const { t } = useTranslation();

  const statusChip = () => {
    if (!subscription) {
      return <Chip size="small" label={t('billing.subscription.noPlan')} />;
    }
    if (subscription.status === SubscriptionStatus.PastDue) {
      return (
        <Chip
          size="small"
          color="warning"
          label={t('billing.subscription.status.pastDue')}
        />
      );
    }
    if (subscription.status === SubscriptionStatus.Canceled) {
      return (
        <Chip
          size="small"
          color="error"
          label={t('billing.subscription.status.canceled')}
        />
      );
    }
    if (subscription.status === SubscriptionStatus.Active) {
      return (
        <Chip
          size="small"
          color="success"
          label={t('billing.subscription.status.active')}
        />
      );
    }
    return (
      <Chip size="small" label={t('billing.subscription.status.incomplete')} />
    );
  };

  const canManageInStripe = !!subscription?.hasStripeSubscription;

  return (
    <Box
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        useFlexGap
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {plan
                ? t('billing.subscription.onPlan', { plan: plan.name })
                : t('billing.subscription.noPlanHeader')}
            </Typography>
            {statusChip()}
          </Stack>

          {subscription?.currentPeriodEnd && (
            <Typography variant="body2" color="text.secondary">
              {t(
                subscription.cancelAtPeriodEnd
                  ? 'billing.subscription.endsOn'
                  : 'billing.subscription.renewsOn',
                {
                  date: dayjs(subscription.currentPeriodEnd).format(
                    DISPLAY_DATE_FORMAT,
                  ),
                },
              )}
            </Typography>
          )}

          {!subscription && (
            <Typography variant="body2" color="text.secondary">
              {t('billing.subscription.freeHint')}
            </Typography>
          )}
        </Box>

        {canManageInStripe && (
          <Stack direction="row" spacing={1} flexShrink={0}>
            <Button
              variant="outlined"
              size="small"
              disabled={isPortalPending}
              onClick={onOpenPortal}
            >
              {t('billing.subscription.manage')}
            </Button>
            {!subscription.cancelAtPeriodEnd && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                disabled={isCancelPending}
                onClick={onCancel}
              >
                {t('billing.subscription.cancel')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
